import { useState, useEffect } from "react";
import loadingSpinner from "@/assets/loading-spinner.gif";
import { Button } from "./button";
import { RefreshCw } from "lucide-react";

interface LoadingScreenProps {
  message?: string;
  transparent?: boolean;
  /** Time in ms before showing retry button (default: 10000) */
  showRetryAfter?: number;
}

export function LoadingScreen({ 
  message = "Laddar...", 
  transparent = false,
  showRetryAfter = 10000 
}: LoadingScreenProps) {
  const [showRetry, setShowRetry] = useState(false);
  const [dots, setDots] = useState('');
  
  // Show retry button after timeout
  useEffect(() => {
    const timeout = setTimeout(() => {
      setShowRetry(true);
    }, showRetryAfter);
    
    return () => clearTimeout(timeout);
  }, [showRetryAfter]);
  
  // Animate dots for visual feedback
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 500);
    
    return () => clearInterval(interval);
  }, []);
  
  const handleRetry = () => {
    // Clear caches and reload
    if ('caches' in window) {
      caches.keys().then(keys => {
        keys.forEach(key => caches.delete(key));
      });
    }
    window.location.reload();
  };
  
  return (
    <div className={`min-h-screen flex items-center justify-center ${transparent ? 'bg-transparent' : 'backdrop-blur-md'}`}>
      <div className="flex flex-col items-center gap-4">
        <img 
          src={loadingSpinner} 
          alt="Laddar" 
          className="h-24 w-24"
        />
        <p className="text-foreground text-sm font-light min-w-[100px] text-center">
          {message}{dots}
        </p>
        
        {showRetry && (
          <div className="flex flex-col items-center gap-2 mt-4 animate-in fade-in duration-300">
            <p className="text-muted-foreground text-xs">
              Tar det för lång tid?
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleRetry}
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Ladda om
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
